import React, { useEffect, useState, useMemo, useRef } from "react";
import { Layout } from "antd";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import SidebarMenu from "../components/SidebarMenu/SidebarMenu";
import { Module } from "../types/Module";
import { customThemes } from "../themeConfig";

const { Sider } = Layout;

type SidebarProps = {
  collapsed: boolean;
  darkMode: boolean;
  isMobile: boolean;

  // ✅ ADD THIS
  themeStyles: {
    siderBg: string;
    headerBg: string;
    layoutBg: string;
    contentBg: string;
    contentText: string;
    footerBg: string;
    colorPrimary: string;
  };
};

const Sidebar: React.FC<SidebarProps> = ({ collapsed, darkMode, isMobile }) => {
  const location = useLocation();

  const [selectedKey, setSelectedKey] = useState<string>("1");
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // Redux modules
  const rawModules = useSelector(
    (state: any) => state.user?.modules as Module[],
  );

  const modules = useMemo<Module[]>(() => {
    return rawModules?.filter((m: any) => m?.module_type === "page" && m?.module_show_on_menu === "Y") || [];
  }, [rawModules]);

  const user = useSelector((state: any) => state.user?.user);

  const logo_url =
    user?.organization_logo && process.env.REACT_APP_FILE_URL
      ? `${process.env.REACT_APP_FILE_URL}uploads/organizations/logo/${user.organization_logo}`
      : "";

  const currentPath = location.pathname;
  const themeStyles = darkMode ? customThemes.dark : customThemes.light;

  const siderRef = useRef<HTMLDivElement>(null);

  // ✅ Selected menu & Auto-open active submenu parents
  useEffect(() => {
    if (!modules || modules.length === 0) return;

    const isPathActive = (moduleUrl: string, currentPath: string): boolean => {
      if (!moduleUrl) return false;
      const normalizedUrl = moduleUrl.replace(/\/$/, "");
      const normalizedPath = currentPath.replace(/\/$/, "");
      if (normalizedUrl === "") {
        return normalizedPath === "";
      }
      return normalizedPath === normalizedUrl || normalizedPath.startsWith(normalizedUrl + "/");
    };

    // 1. Find the best matching module from the flat list
    let bestMatch: Module | null = null;
    let bestMatchLen = -1;

    for (const mod of modules) {
      if (isPathActive(mod.module_url, currentPath)) {
        if (mod.module_url === currentPath) {
          bestMatch = mod;
          break;
        }
        if (mod.module_url.length > bestMatchLen) {
          bestMatch = mod;
          bestMatchLen = mod.module_url.length;
        }
      }
    }

    if (bestMatch) {
      // 2. Set the selected key to the matched module's ID
      setSelectedKey(String(bestMatch.module_id));

      // 3. Trace all ancestors to automatically open parent submenus
      const ancestors: string[] = [];
      let currentParentId = bestMatch.module_parent;
      while (currentParentId && currentParentId !== 0) {
        const parentMod = modules.find((m) => m.module_id === currentParentId);
        if (parentMod) {
          ancestors.push(String(parentMod.module_id));
          currentParentId = parentMod.module_parent;
        } else {
          break;
        }
      }

      // Automatically open these parent submenus
      setOpenKeys((prev) => {
        const merged = new Set([...prev, ...ancestors]);
        return Array.from(merged);
      });
    }
  }, [currentPath, modules]);

  // ✅ Custom handler for manual submenu click (accordion-like behavior)
  const handleOpenChange = (keys: string[]) => {
    // Find all module_ids that are parents of other modules
    const parentIds = new Set(modules.map((m) => m.module_parent).filter((id) => id !== 0));

    // Root-level submenu keys: module_parent === 0 and is in parentIds
    const rootSubmenuKeys = modules
      .filter((m) => m.module_parent === 0 && parentIds.has(m.module_id))
      .map((m) => String(m.module_id));

    // Find the latest opened key
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));

    if (latestOpenKey && rootSubmenuKeys.includes(latestOpenKey)) {
      // Keep only the latest opened root submenu
      setOpenKeys([latestOpenKey]);
    } else {
      setOpenKeys(keys);
    }
  };

  return (
    <Sider
      ref={siderRef}
      trigger={null}
      width={296}
      collapsible
      collapsed={collapsed}
      breakpoint="lg"
      collapsedWidth={isMobile ? 0 : 70}
      theme={darkMode ? "light" : "dark"}
      style={{
        overflow: "auto",
        height: "100vh",
        position: "sticky",
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        scrollbarWidth: "thin",
        scrollbarGutter: "stable",
        backgroundColor: themeStyles.siderBg,
      }}
    >
      {/* Logo */}
      <div
        style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={
            collapsed
              ? logo_url || "/svicon-small-logo.png"
              : logo_url || "/svicon-logo.png"
          }
          alt="Logo"
          style={{
            height: 42,
            transition: "all 0.3s ease",
            objectFit: "contain",
          }}
        />
      </div>

      {/* Sidebar Menu */}
      <SidebarMenu
        modules={modules}
        darkMode={darkMode}
        siderRef={siderRef}
        collapsed={collapsed}
        selectedKey={selectedKey}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
      />
    </Sider>
  );
};

export default Sidebar;
